import { Link } from "react-router-dom";
import { Phone, MapPin, Clock } from "lucide-react";
import { Logo } from "./Logo";
import { WhatsAppIcon, InstagramIcon } from "./Icons";
import { useSite } from "../../context/SiteContext";
import { GoogleRating } from "./GoogleRating";
import { BRAND, SLOGAN, TEL, WA, IG, IG_HANDLE, PHONE_DISPLAY, ADDRESS, MAPS_DIR, FOOTER_LINKS } from "../../lib/site";

const ext = { target: "_blank", rel: "noopener noreferrer" };

const Item = ({ href, icon: Icon, children, testId, external }) => (
  <a href={href} data-testid={testId} {...(external ? ext : {})} className="group flex items-start gap-3 text-sm text-white/80 transition-colors hover:text-gold-light">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={1.8} />
    <span>{children}</span>
  </a>
);

const SOCIAL = [
  { href: IG, icon: InstagramIcon, label: "Instagram", id: "instagram", external: true },
  { href: WA, icon: WhatsAppIcon, label: "WhatsApp", id: "whatsapp", external: true },
  { href: TEL, icon: Phone, label: "Telefon", id: "phone" },
  { href: MAPS_DIR, icon: MapPin, label: "Konum", id: "location", external: true },
];

const Brand = () => {
  const { settings } = useSite();
  return (
    <div className="space-y-5">
      <Logo className="h-24 sm:h-28" badge testId="footer-logo" />
      <div>
        <p className="text-lg font-semibold">{BRAND}</p>
        <p className="mt-1 text-sm text-gold-light/90">{SLOGAN}</p>
      </div>
      <p className="max-w-sm text-sm leading-relaxed text-white/65">
        Sivas Merkez’de hijyenik, konforlu ve profesyonel masaj &amp; spa deneyimi. Kendinize zaman ayırın, Lunessa’da yenilenin.
      </p>
      {settings.campaign_active && settings.campaign_badge && (
        <span className="inline-flex items-center rounded-full border border-gold/60 px-3.5 py-1 text-xs font-semibold text-gold-light" data-testid="footer-campaign-badge">
          Tüm Paketlerde {settings.campaign_badge}
        </span>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <a href={WA} {...ext} className="btn-gold !px-5 !py-2.5 text-[13px]" data-testid="footer-randevu-btn"><WhatsAppIcon className="h-4 w-4" /> Randevu Al</a>
        <div className="flex gap-2">
          {SOCIAL.map((s) => (
            <a key={s.id} href={s.href} aria-label={s.label} {...(s.external ? ext : {})} data-testid={`footer-social-${s.id}`} className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/35 text-gold-light transition-colors hover:border-gold hover:bg-gold/10">
              <s.icon className="h-4 w-4" strokeWidth={1.8} />
            </a>
          ))}
        </div>
      </div>
      <GoogleRating compact dark testId="footer-google-rating" />
    </div>
  );
};

export const Footer = () => {
  const { settings } = useSite();
  return (
    <footer data-testid="site-footer" className="relative overflow-hidden text-white" style={{ background: "linear-gradient(180deg,#022C22 0%,#011B15 100%)" }}>
      <div className="gold-rule" />
      <div className="container-lux grid gap-12 py-16 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr] lg:gap-16 lg:py-20">
        <Brand />
        <div>
          <h3 className="mb-5 text-xs font-medium uppercase tracking-[0.22em] text-gold">İletişim</h3>
          <div className="space-y-4">
            <Item href={TEL} icon={Phone} testId="footer-phone">{PHONE_DISPLAY}</Item>
            <Item href={WA} icon={WhatsAppIcon} testId="footer-whatsapp" external>WhatsApp: {PHONE_DISPLAY}</Item>
            <Item href={IG} icon={InstagramIcon} testId="footer-instagram" external>{IG_HANDLE}</Item>
            <Item href={MAPS_DIR} icon={MapPin} testId="footer-address" external>{ADDRESS}</Item>
            {settings.working_hours && (
              <div className="flex items-start gap-3 text-sm text-white/80" data-testid="footer-hours">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={1.8} />
                <span><span className="block text-xs uppercase tracking-[0.18em] text-white/50">Çalışma Saatleri</span>{settings.working_hours}</span>
              </div>
            )}
          </div>
        </div>
        <div>
          <h3 className="mb-5 text-xs font-medium uppercase tracking-[0.22em] text-gold">Hızlı Bağlantılar</h3>
          <ul className="grid grid-cols-2 gap-x-4 gap-y-3 lg:grid-cols-1">
            {FOOTER_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-sm text-white/75 transition-colors hover:text-gold-light" data-testid={`footer-link-${l.to.replace(/\//g, "") || "home"}`}>{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="gold-rule opacity-60" />
      <div className="container-lux flex flex-col items-center gap-2 py-6 pb-24 text-center text-xs text-white/55 md:flex-row md:justify-between md:pb-6 md:text-left">
        <span data-testid="footer-copyright">© Lunessa Spa Masaj – Tüm Hakları Saklıdır.</span>
        <a href="https://wa.me/905324751871" target="_blank" rel="nofollow noopener noreferrer" className="text-[11px] text-white/40 transition-colors duration-300 hover:text-gold-light" data-testid="footer-credit">
          Web Sitesi ve Dijital Çözümler: <span className="font-medium">Bana Bi Media</span>
        </a>
      </div>
    </footer>
  );
};
